/**
 * Les erreurs de SAISIE de la table transverse (`api-error-transverse.ts`, qui les ré-exporte) :
 * un 422 de Pydantic, dit dans la langue de l'écran.
 *
 * Le corps porte `detail: [{type, loc, msg, ctx}]`. `msg` est anglais ; `type` et `ctx` (la borne)
 * suffisent à écrire la phrase dans la langue de l'écran. Un corps qui porte plusieurs erreurs les
 * dit TOUTES : n'en montrer qu'une faisait corriger, renvoyer, et découvrir la suivante.
 *
 * ⚠️ Les clés sont écrites EN ENTIER dans l'appel à `t` : le gardien `i18n-keys-exist` ne lit que
 * les appels.
 */

import type { TFunction } from 'i18next'

/** Une erreur de validation telle que la rend `exception_handlers.py`. */
export interface EntreeDeValidation {
    type?: string
    loc?: unknown[]
    msg?: string
    ctx?: Record<string, unknown>
}

/** Une erreur Pydantic dite par son TYPE, la borne (`ctx`) placée dans la phrase. */
export function phraseDEntreeDeValidation(entree: EntreeDeValidation, t: TFunction): string {
    const ctx = entree.ctx ?? {}
    switch (entree.type) {
        case 'missing':
            return t('common.refusTransverses.saisie.obligatoire', 'Un champ obligatoire n’est pas renseigné.')
        case 'greater_than':
            return t('common.refusTransverses.saisie.superieurA', {
                limite: ctx.gt,
                defaultValue: 'Une valeur saisie doit être supérieure à {{limite, number}}.',
            })
        case 'greater_than_equal':
            return t('common.refusTransverses.saisie.auMoins', {
                limite: ctx.ge,
                defaultValue: 'Une valeur saisie doit être supérieure ou égale à {{limite, number}}.',
            })
        case 'less_than':
            return t('common.refusTransverses.saisie.inferieurA', {
                limite: ctx.lt,
                defaultValue: 'Une valeur saisie doit être inférieure à {{limite, number}}.',
            })
        case 'less_than_equal':
            return t('common.refusTransverses.saisie.auPlus', {
                limite: ctx.le,
                defaultValue: 'Une valeur saisie doit être inférieure ou égale à {{limite, number}}.',
            })
        case 'string_too_short':
            return t('common.refusTransverses.saisie.texteTropCourt', {
                limite: ctx.min_length,
                defaultValue: 'Un texte saisi doit compter au moins {{limite, number}} caractère(s).',
            })
        case 'string_too_long':
            return t('common.refusTransverses.saisie.texteTropLong', {
                limite: ctx.max_length,
                defaultValue: 'Un texte saisi ne peut pas dépasser {{limite, number}} caractères.',
            })
        case 'too_short':
            return t('common.refusTransverses.saisie.listeTropCourte', {
                limite: ctx.min_length,
                defaultValue: 'Une liste doit compter au moins {{limite, number}} élément(s).',
            })
        case 'too_long':
            return t('common.refusTransverses.saisie.listeTropLongue', {
                limite: ctx.max_length,
                defaultValue: 'Une liste ne peut pas dépasser {{limite, number}} éléments.',
            })
        case 'int_parsing':
        case 'int_type':
        case 'int_from_float':
        case 'float_parsing':
        case 'float_type':
        case 'decimal_parsing':
        case 'decimal_type':
        case 'finite_number':
            return t('common.refusTransverses.saisie.nombre', 'Une valeur saisie doit être un nombre.')
        case 'date_parsing':
        case 'date_type':
        case 'date_from_datetime_parsing':
        case 'datetime_parsing':
        case 'datetime_type':
            return t('common.refusTransverses.saisie.date', 'Une date saisie n’est pas valide.')
        case 'enum':
        case 'literal_error':
            return t(
                'common.refusTransverses.saisie.choix',
                'Une valeur choisie ne fait pas partie de celles proposées : rechargez la page et refaites votre choix.',
            )
        default:
            return t(
                'common.refusTransverses.saisie.refusee',
                'Une valeur saisie n’est pas acceptée : vérifiez les champs du formulaire.',
            )
    }
}

/** Les erreurs d'un 422, TOUTES dites (`dire` rend la phrase d'une erreur), sans doublon. */
export const direLesEntrees = (entrees: EntreeDeValidation[], dire: (entree: EntreeDeValidation) => string): string =>
    [...new Set(entrees.map(dire))].join(' ')
